import React, { useEffect, useState } from 'react';
import { ArrowLeft, LifeBuoy, Mail, MessageSquare, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../lib/api';
import FeatureHeader from './FeatureHeader';

export default function HelpSupport({ onBack }) {
  const [faqs, setFaqs] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [openFaqId, setOpenFaqId] = useState('');
  const [feedback, setFeedback] = useState('');
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState('other');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });

  const showStatus = (type, message) => {
    setStatus({ type, message });
    if (type === 'success') {
      window.setTimeout(() => {
        setStatus((prev) => (prev.message === message ? { type: '', message: '' } : prev));
      }, 2600);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [faqRes, ticketRes] = await Promise.all([
        api.get('/api/support/faqs'),
        api.get('/api/support/tickets'),
      ]);
      setFaqs(faqRes.data?.faqs || []);
      setTickets(ticketRes.data?.tickets || []);
      if ((faqRes.data?.faqs || []).length && !openFaqId) {
        setOpenFaqId(faqRes.data.faqs[0].id);
      }
    } catch {
      showStatus('error', 'Failed to load help center data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSubmit = async () => {
    if (!subject.trim()) {
      showStatus('error', 'Please enter a subject');
      return;
    }
    if (!feedback.trim()) {
      showStatus('error', 'Please enter your message');
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post('/api/support/tickets', {
        category,
        subject: subject.trim(),
        message: feedback.trim(),
      });

      if (res.data?.ticket) {
        setTickets((prev) => [res.data.ticket, ...prev]);
      }
      setSubject('');
      setFeedback('');
      setCategory('other');
      showStatus('success', 'Support ticket submitted successfully');
    } catch (err) {
      showStatus('error', err?.response?.data?.message || 'Failed to submit support ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const supportEmail = 'support@qureohealth.com';

  return (
    <div style={pageStyle}>
      <FeatureHeader title="Help & Support" onBack={onBack} />

      <div style={cardStyle}>
        <LifeBuoy size={22} style={{ color: 'var(--primary)' }} />
        <div style={{ flex: 1 }}>
          <div style={cardTitle}>FAQ & Tutorials</div>
          <div style={cardText}>Find quick answers to common questions.</div>
        </div>
      </div>

      {loading ? (
        <div style={{ fontSize: '0.9rem', color: '#666' }}>Loading FAQs...</div>
      ) : (
        <div style={faqWrapStyle}>
          {faqs.map((item) => {
            const isOpen = openFaqId === item.id;
            return (
              <div key={item.id} style={faqItemStyle}>
                <button
                  style={faqQuestionBtnStyle}
                  onClick={() => setOpenFaqId(isOpen ? '' : item.id)}
                >
                  <span>{item.question}</span>
                  {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
                {isOpen && <div style={faqAnswerStyle}>{item.answer}</div>}
              </div>
            );
          })}
        </div>
      )}

      <div style={{ ...cardStyle, marginTop: 16 }}>
        <Mail size={22} style={{ color: 'var(--primary)' }} />
        <div style={{ flex: 1 }}>
          <div style={cardTitle}>Contact Support</div>
          <div style={cardText}>Need direct help? Reach us via email.</div>
        </div>
        <button
          style={contactBtn}
          onClick={() => {
            window.location.href = `mailto:${supportEmail}?subject=${encodeURIComponent('Qureo Support Request')}`;
          }}
        >
          Email Us
        </button>
      </div>

      <div style={{ marginTop: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <MessageSquare size={20} style={{ color: 'var(--primary)' }} />
          <div style={{ fontWeight: '600' }}>Submit a Support Ticket</div>
        </div>

        <label style={labelStyle}>Category</label>
        <select value={category} onChange={(e) => setCategory(e.target.value)} style={inputStyle}>
          <option value="account">Account</option>
          <option value="billing">Billing</option>
          <option value="technical">Technical</option>
          <option value="medical-records">Medical Records</option>
          <option value="other">Other</option>
        </select>

        <label style={labelStyle}>Subject</label>
        <input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Short summary of your issue"
          style={inputStyle}
        />

        <label style={labelStyle}>Message</label>
        <textarea
          placeholder="Describe your issue in detail..."
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          style={textareaStyle}
        />

        <button onClick={handleSubmit} style={buttonStyle} disabled={submitting}>
          {submitting ? 'Submitting...' : 'Submit Ticket'}
        </button>

        {status.message && (
          <div style={{ ...toastStyle, color: status.type === 'error' ? '#b42318' : '#15803d' }}>
            {status.message}
          </div>
        )}
      </div>

      <div style={{ marginTop: 24 }}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>Recent Tickets</div>
        <div style={ticketTableWrap}>
          <table style={ticketTable}>
            <thead>
              <tr>
                <th style={thStyle}>Subject</th>
                <th style={thStyle}>Category</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Created</th>
              </tr>
            </thead>
            <tbody>
              {tickets.length === 0 ? (
                <tr>
                  <td colSpan={4} style={tdStyle}>No tickets yet.</td>
                </tr>
              ) : (
                tickets.map((t) => (
                  <tr key={t._id}>
                    <td style={tdStyle}>{t.subject}</td>
                    <td style={tdStyle}>{t.category}</td>
                    <td style={tdStyle}>{t.status}</td>
                    <td style={tdStyle}>{new Date(t.createdAt).toLocaleString()}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const pageStyle = {
  minHeight: '100vh',
  padding: '24px 16px',
  background: 'var(--bg)',
  fontFamily: 'sans-serif',
  color: 'var(--on-bg)',
};

const headerStyle = {
  fontSize: '1.25rem',
  fontWeight: '700',
};

const backBtnStyle = {
  background: 'transparent',
  border: 'none',
  padding: 0,
  marginRight: 8,
  cursor: 'pointer',
};

const cardStyle = {
  background: 'var(--surface)',
  borderRadius: 12,
  padding: 16,
  marginBottom: 8,
  boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  justifyContent: 'space-between',
};

const cardTitle = {
  fontSize: '0.95rem',
  fontWeight: '600',
};

const cardText = {
  fontSize: '0.75rem',
  color: '#666',
};

const faqWrapStyle = {
  background: 'var(--surface)',
  borderRadius: 12,
  boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
  overflow: 'hidden',
};

const faqItemStyle = {
  borderBottom: '1px solid #eef2f7',
};

const faqQuestionBtnStyle = {
  width: '100%',
  border: 'none',
  background: 'transparent',
  padding: '12px 14px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  fontSize: '0.86rem',
  fontWeight: 600,
  cursor: 'pointer',
  textAlign: 'left',
};

const faqAnswerStyle = {
  padding: '0 14px 12px 14px',
  fontSize: '0.83rem',
  color: '#4b5563',
  lineHeight: 1.4,
};

const contactBtn = {
  padding: '6px 10px',
  fontSize: '0.75rem',
  background: '#eee',
  borderRadius: 8,
  border: 'none',
  color: '#333',
  cursor: 'pointer',
};

const labelStyle = {
  fontSize: '0.85rem',
  marginTop: 12,
  marginBottom: 6,
  display: 'block',
};

const inputStyle = {
  width: '100%',
  padding: 10,
  borderRadius: 8,
  border: '1px solid #ccc',
  background: 'var(--surface)',
  color: 'var(--on-bg)',
  fontSize: '0.9rem',
};

const textareaStyle = {
  width: '100%',
  minHeight: 90,
  padding: 10,
  borderRadius: 8,
  border: '1px solid #ccc',
  background: 'var(--surface)',
  color: 'var(--on-bg)',
  fontSize: '0.9rem',
  marginBottom: 12,
};

const buttonStyle = {
  padding: '10px 16px',
  background: 'var(--primary)',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  fontSize: '0.9rem',
  fontWeight: '500',
  cursor: 'pointer',
};

const toastStyle = {
  marginTop: 12,
  fontSize: '0.85rem',
};

const ticketTableWrap = {
  overflowX: 'auto',
  background: 'var(--surface)',
  borderRadius: 10,
  boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
};

const ticketTable = {
  width: '100%',
  borderCollapse: 'collapse',
  minWidth: 560,
};

const thStyle = {
  textAlign: 'left',
  fontSize: '0.76rem',
  fontWeight: 700,
  color: '#475467',
  padding: '10px 12px',
  borderBottom: '1px solid #e5e7eb',
  background: '#f8fafc',
};

const tdStyle = {
  fontSize: '0.82rem',
  color: '#111827',
  padding: '10px 12px',
  borderBottom: '1px solid #f1f5f9',
};
